<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";

import { api, ApiError } from "@/api/client";
import { useVisualViewport } from "@/composables/useVisualViewport";
import { describeError as friendlyError } from "@/utils/errors";
import { terminalOutput, terminalSocketUrl } from "@/terminalStream";
import { terminalLiveness } from "@/terminalLiveness";
import { terminalClipboard } from "@/terminalClipboard";

import "xterm/css/xterm.css";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const terminalEl = ref<HTMLDivElement | null>(null);
const rootEl = ref<HTMLElement | null>(null);
const sessionId = route.params.sessionId as string;
const fullscreenKey = `nyx:terminal:${sessionId}:expanded`;
const fullscreen = ref(readExpanded());
const viewport = useVisualViewport();
const nativeFullscreen = ref(false);
const orientationLocked = ref(false);
const orientationMessage = ref("");
const pasteOpen = ref(false);
const pasteDraft = ref("");
const pasteEl = ref<HTMLTextAreaElement | null>(null);
const orientation = screen.orientation as ScreenOrientation & {
  lock?: (value: string) => Promise<void>;
};
const canLockOrientation = typeof orientation?.lock === "function";
const canNativeFullscreen = typeof document.documentElement.requestFullscreen === "function";
const standalone = window.matchMedia("(display-mode: standalone)").matches;
const status = ref<"connecting" | "connected" | "closed" | "error">("connecting");
const errorMessage = ref<string | null>(null);
const imageMessage = ref("");
const imageError = ref(false);
const stopping = ref(false);

const term = shallowRef<Terminal | null>(null);
const fit = shallowRef<FitAddon | null>(null);
let socket: WebSocket | null = null;
let output: ReturnType<typeof terminalOutput> | null = null;
let liveness: ReturnType<typeof terminalLiveness> | null = null;
let imageClipboard: ReturnType<typeof terminalClipboard> | null = null;
let resizeObserver: ResizeObserver | null = null;
let detachTouch: (() => void) | null = null;
let agentId: string | null = null;
let targetType: "agent" | "nx" = "agent";
let disposed = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 1000;
let reconnecting = false;
let frame = 0;
let layoutTimers: ReturnType<typeof setTimeout>[] = [];

// §10.8 binary framing
const FRAME_INPUT = 0x01;
const FRAME_RESIZE = 0x03;

// --- mobile interaction state --------------------------------------
// Touch devices get a "reading-first" model: the terminal fills the
// screen and the soft keyboard never appears on its own. A floating
// button reveals the accessory bar (Esc/Ctrl/arrows/^C…), and only an
// explicit ⌨ tap raises the OS keyboard. Finger drag scrolls the
// terminal's own scrollback (handled natively by xterm).
const isMobile = ref(false);
const barOpen = ref(false);
// Whether the OS keyboard is currently summoned (so ⌨ toggles it off too).
const keyboardOn = ref(false);
const sessionLabel = ref("");

onMounted(async () => {
  isMobile.value = window.matchMedia("(hover: none) and (pointer: coarse)").matches;

  // Resolve the session to know which agent the stop button targets,
  // and bail out fast if it's gone (404 → back to dashboard).
  try {
    const all = await api.sessions.listAll();
    const found = all.sessions.find((s) => s.id === sessionId);
    if (!found) {
      errorMessage.value = t("terminal.not_found");
      status.value = "error";
      return;
    }
    if (disposed) return;
    if (found.status === "stopped") {
      status.value = "closed";
      errorMessage.value = t("terminal.not_found");
      return;
    }
    agentId = found.agent_id;
    targetType = found.target_type;
    sessionLabel.value = found.name || found.engine || sessionId.slice(0, 8);
  } catch (err) {
    if (disposed) return;
    errorMessage.value = describeError(err);
    status.value = "error";
    return;
  }

  if (disposed || !terminalEl.value) return;

  const t1 = new Terminal({
    convertEol: false,
    cursorBlink: true,
    // Smaller on phones so more columns fit (full-width TUIs need the
    // breadth); a generous scrollback so finger/wheel scroll has history to
    // move through (§ reading is the primary mobile use).
    fontSize: isMobile.value ? 11 : 13,
    scrollback: 5000,
    fontFamily: 'ui-monospace, "JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
    theme: xtermTheme(),
  });
  const fitAddon = new FitAddon();
  t1.loadAddon(fitAddon);
  t1.open(terminalEl.value);
  fitAddon.fit();
  term.value = t1;
  fit.value = fitAddon;
  if (targetType === "agent") {
    imageClipboard = terminalClipboard(
      terminalEl.value,
      t1,
      sessionId,
      () => socket?.readyState === WebSocket.OPEN,
      (uploading, error) => {
        imageError.value = !!error;
        imageMessage.value = error
          ? describeError(error)
          : uploading
            ? t("terminal.image_uploading")
            : "";
      },
    );
  }

  // On touch, keep the OS keyboard suppressed until the user asks for it
  // via ⌨ — a plain tap should scroll/select, not summon the keyboard.
  if (isMobile.value) {
    setKeyboardEnabled(false);
    detachTouch = setupTouchScroll(terminalEl.value);
  }

  connectStream();

  // Forward terminal input (soft keyboard / hardware) straight to the PTY.
  t1.onData((data) => sendInput(data));

  // Forward terminal resize → resize frame (cols/rows as big-endian u16).
  t1.onResize(() => sendResize());

  // Refit on container resize (covers the accessory bar opening/closing
  // and the keyboard shrinking the visual viewport).
  resizeObserver = new ResizeObserver(() => refit());
  resizeObserver.observe(terminalEl.value);

  window.addEventListener("nyx-theme", onThemeChange);
  document.addEventListener("fullscreenchange", onFullscreenChange);
  window.addEventListener("orientationchange", onOrientation);
  window.addEventListener("resize", settleLayout);
  window.addEventListener("online", onResume);
  window.addEventListener("pageshow", onResume);
  document.addEventListener("visibilitychange", onVisibility);
  document.addEventListener("resume", onResume);
  document.addEventListener("keydown", onEscape);
  void document.fonts?.ready.then(() => {
    if (!disposed) settleLayout();
  });
  settleLayout();
});

onBeforeUnmount(() => {
  disposed = true;
  output?.dispose();
  liveness?.dispose();
  imageClipboard?.dispose();
  clearReconnect();
  clearTimeout(connectTimer ?? undefined);
  cancelAnimationFrame(frame);
  layoutTimers.forEach(clearTimeout);
  window.removeEventListener("online", onResume);
  window.removeEventListener("pageshow", onResume);
  document.removeEventListener("visibilitychange", onVisibility);
  document.removeEventListener("resume", onResume);
  document.removeEventListener("keydown", onEscape);
  if (orientationLocked.value) orientation.unlock();
  window.removeEventListener("nyx-theme", onThemeChange);
  document.removeEventListener("fullscreenchange", onFullscreenChange);
  window.removeEventListener("orientationchange", onOrientation);
  window.removeEventListener("resize", settleLayout);
  if (detachTouch) {
    detachTouch();
    detachTouch = null;
  }
  if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (socket) {
    socket.close();
    socket = null;
  }
  if (term.value) {
    term.value.dispose();
    term.value = null;
  }
});

// Vue must commit the new bounds/bar layout before xterm measures it.
watch([barOpen, fullscreen, pasteOpen, viewport.bounds], () => refit(), { flush: "post" });
watch(fullscreen, (value) => {
  try {
    sessionStorage.setItem(fullscreenKey, String(value));
  } catch {
    /* storage unavailable */
  }
});

function readExpanded() {
  try {
    return sessionStorage.getItem(fullscreenKey) === "true";
  } catch {
    return false;
  }
}

function sendResize() {
  const current = term.value;
  if (!current || socket?.readyState !== WebSocket.OPEN) return;
  const data = new Uint8Array(5);
  data[0] = FRAME_RESIZE;
  new DataView(data.buffer).setUint16(1, current.cols, false);
  new DataView(data.buffer).setUint16(3, current.rows, false);
  socket.send(data);
}

function refit() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => {
    if (disposed || document.visibilityState === "hidden") return;
    const el = terminalEl.value;
    if (!el || el.clientWidth < 1 || el.clientHeight < 1) return;
    try {
      fit.value?.fit();
      term.value?.refresh(0, term.value.rows - 1);
      // Explicitly resend even if xterm already fitted while disconnected.
      sendResize();
    } catch {
      /* another resize/font event will retry once layout is available */
    }
  });
}

function settleLayout() {
  viewport.refresh();
  refit();
  layoutTimers.forEach(clearTimeout);
  layoutTimers = [100, 300, 700].map((delay) => setTimeout(refit, delay));
}

function onOrientation() {
  settleLayout();
}

// Translate finger drags into wheel events. xterm then does the right thing per
// mode: scroll its scrollback (shell) or forward a mouse-wheel to a full-screen
// app (tmux/zellij with mouse mode). Includes simple inertia (momentum).
function setupTouchScroll(el: HTMLElement): () => void {
  const LINE = 16; // px ≈ one wheel tick / row
  let lastY: number | null = null;
  let lastT = 0;
  let velocity = 0; // px/ms
  let accum = 0;
  let inertia = 0;
  let posX = 0;
  let posY = 0;

  // One wheel tick — synthesize a REAL WheelEvent on the element under the
  // finger and let xterm encode it exactly as it does for a desktop mouse
  // wheel: scrolls scrollback for shell, or forwards the correct mouse-wheel
  // sequence to a full-screen app (tmux/zellij) per the protocol it requested.
  // Letting xterm encode avoids guessing SGR vs legacy. dir>0 = down/forward.
  const wheelEvent = (dir: number) => {
    const target = (document.elementFromPoint(posX, posY) as HTMLElement | null) ?? el;
    target.dispatchEvent(
      new WheelEvent("wheel", {
        // Line mode (deltaMode 1), ±1 line per tick: xterm converts this to a
        // reliable wheel click. Pixel-mode deltas this small were rounded to
        // ~0 lines for the mouse protocol, which made scrolling crawl.
        deltaY: dir,
        deltaMode: 1,
        clientX: posX,
        clientY: posY,
        bubbles: true,
        cancelable: true,
      }),
    );
  };
  const scrollTick = (dir: number) => {
    const t = term.value;
    if (!t) return;
    if (t.modes.mouseTrackingMode !== "none") {
      // Mouse mode known (fresh from replay) — let xterm encode the wheel in
      // the exact protocol the app requested (SGR/legacy/urxvt).
      wheelEvent(dir);
    } else if (t.buffer.active.type === "alternate") {
      // Full-screen app but its mouse-enable scrolled out of the replay buffer
      // on reattach, so xterm thinks mouse is off. The app (tmux/zellij) is
      // still in mouse mode on the agent — send an SGR wheel directly so it
      // keeps scrolling. (SGR is what crossterm / modern tmux negotiate.)
      sendInput(`\x1b[<${dir > 0 ? 65 : 64};1;1M`);
    } else {
      // Normal buffer (shell): scroll xterm's own scrollback.
      wheelEvent(dir);
    }
  };
  const emit = (dy: number) => {
    accum += dy;
    while (Math.abs(accum) >= LINE) {
      const dir = accum > 0 ? 1 : -1;
      scrollTick(dir);
      accum -= dir * LINE;
    }
  };
  const stopInertia = () => {
    if (inertia) cancelAnimationFrame(inertia);
    inertia = 0;
  };
  const onStart = (e: TouchEvent) => {
    stopInertia();
    const tch = e.touches[0];
    lastY = tch?.clientY ?? null;
    posX = tch?.clientX ?? posX;
    posY = tch?.clientY ?? posY;
    lastT = e.timeStamp;
    velocity = 0;
  };
  const onMove = (e: TouchEvent) => {
    if (lastY == null) return;
    const tch = e.touches[0];
    const y = tch?.clientY ?? lastY;
    posX = tch?.clientX ?? posX;
    posY = y;
    const dy = lastY - y;
    const dt = e.timeStamp - lastT || 16;
    velocity = dy / dt;
    lastY = y;
    lastT = e.timeStamp;
    if (dy) {
      emit(dy);
      e.preventDefault();
    }
  };
  const onEnd = () => {
    lastY = null;
    let v = velocity * 16; // px/frame
    if (Math.abs(v) <= 2) return;
    const step = () => {
      v *= 0.93;
      if (Math.abs(v) < 0.4) {
        inertia = 0;
        return;
      }
      emit(v);
      inertia = requestAnimationFrame(step);
    };
    inertia = requestAnimationFrame(step);
  };

  // Capture phase so we intercept before xterm's own touch/selection handlers
  // (otherwise drags over text never reach us — only blank areas scrolled).
  const optC: AddEventListenerOptions = { capture: true, passive: true };
  const optM: AddEventListenerOptions = { capture: true, passive: false };
  el.addEventListener("touchstart", onStart, optC);
  el.addEventListener("touchmove", onMove, optM);
  el.addEventListener("touchend", onEnd, optC);
  el.addEventListener("touchcancel", onEnd, optC);
  return () => {
    stopInertia();
    el.removeEventListener("touchstart", onStart, optC);
    el.removeEventListener("touchmove", onMove, optM);
    el.removeEventListener("touchend", onEnd, optC);
    el.removeEventListener("touchcancel", onEnd, optC);
  };
}

// Expanded layout belongs to the app. Native fullscreen may be revoked by
// the OS when switching apps; that must not discard the user's layout choice.
function requestNativeFullscreen() {
  // The fullscreen element itself is forced to the full layout viewport by
  // the browser's UA stylesheet. Fullscreen the document, leaving the terminal
  // free to shrink/offset inside it when the soft keyboard covers the bottom.
  if (!standalone) void document.documentElement.requestFullscreen?.().catch(() => {});
}
function toggleFullscreen() {
  fullscreen.value = !fullscreen.value;
  if (fullscreen.value) requestNativeFullscreen();
  else {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    if (orientationLocked.value) {
      orientation.unlock();
      orientationLocked.value = false;
    }
  }
  settleLayout();
}
function onFullscreenChange() {
  nativeFullscreen.value = document.fullscreenElement === document.documentElement;
  if (!nativeFullscreen.value && !standalone) orientationLocked.value = false;
  settleLayout();
}
function onEscape(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  if (pasteOpen.value) pasteOpen.value = false;
  else if (fullscreen.value) toggleFullscreen();
}
async function toggleOrientation() {
  orientationMessage.value = "";
  if (orientationLocked.value) {
    orientation.unlock();
    orientationLocked.value = false;
    return;
  }
  try {
    if (!standalone && !document.fullscreenElement) {
      await document.documentElement.requestFullscreen?.();
      fullscreen.value = true;
    }
    await orientation.lock?.(orientation.type);
    orientationLocked.value = true;
  } catch {
    orientationMessage.value = t("terminal.orientation_unavailable");
  }
  settleLayout();
}

// xterm reads the live theme tokens so it follows light/dark (theme.ts).
function xtermTheme() {
  const css = getComputedStyle(document.documentElement);
  const bg = css.getPropertyValue("--color-bg").trim() || "#0d1117";
  const fg = css.getPropertyValue("--color-fg").trim() || "#e6edf3";
  return { background: bg, foreground: fg, cursor: fg };
}
function onThemeChange() {
  if (term.value) {
    term.value.options.theme = xtermTheme();
    refit();
  }
}

// --- input helpers -------------------------------------------------

function sendInput(data: string) {
  if (!socket || socket.readyState !== WebSocket.OPEN || data.length === 0) return;
  const bytes = new TextEncoder().encode(data);
  const frame = new Uint8Array(1 + bytes.length);
  frame[0] = FRAME_INPUT;
  frame.set(bytes, 1);
  socket.send(frame);
}

// --- accessory bar -------------------------------------------------

function toggleBar() {
  barOpen.value = !barOpen.value;
  if (!barOpen.value) {
    setKeyboardEnabled(false);
    keyboardOn.value = false;
    helperTextarea()?.blur();
  }
}

// Dedicated keys send their raw sequence straight to the PTY.
function sendKey(seq: string) {
  sendInput(seq);
}

function helperTextarea(): HTMLTextAreaElement | null {
  return terminalEl.value?.querySelector(".xterm-helper-textarea") ?? null;
}

// `inputmode=none` keeps the OS keyboard hidden even while xterm's hidden
// textarea is focused (so taps scroll, not type). Flip to text to summon.
function setKeyboardEnabled(on: boolean) {
  const ta = helperTextarea();
  if (ta) ta.inputMode = on ? "text" : "none";
}

// Tap ⌨ to raise the OS keyboard; tap again to dismiss it.
function toggleKeyboard() {
  if (keyboardOn.value) {
    keyboardOn.value = false;
    setKeyboardEnabled(false);
    helperTextarea()?.blur();
    return;
  }
  keyboardOn.value = true;
  if (!barOpen.value) barOpen.value = true;
  setKeyboardEnabled(true);
  // focus() inside this tap gesture is what actually raises the keyboard.
  helperTextarea()?.focus();
  term.value?.focus();
}

// Clipboard access needs a user gesture and may be denied. The fallback is
// a normal textarea, so the OS Paste menu works even with xterm's keyboard off.
async function pasteClipboard() {
  try {
    if (imageClipboard && (await imageClipboard.readImages())) return;
  } catch {
    // Native text paste/textarea remains available if image read is denied.
  }
  try {
    const text = await navigator.clipboard.readText();
    if (disposed) return;
    if (socket?.readyState === WebSocket.OPEN) term.value?.paste(text);
    else {
      pasteDraft.value = text;
      pasteOpen.value = true;
    }
  } catch {
    if (disposed) return;
    pasteOpen.value = true;
    await nextTick();
    pasteEl.value?.focus();
  }
}
function submitPaste() {
  if (socket?.readyState !== WebSocket.OPEN || !pasteDraft.value) return;
  term.value?.paste(pasteDraft.value);
  pasteDraft.value = "";
  pasteOpen.value = false;
  // Do not append Enter or refocus the hidden textarea (which raises the OSK).
}

function clearReconnect() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
}
function scheduleReconnect() {
  if (
    disposed ||
    stopping.value ||
    errorMessage.value ||
    document.visibilityState === "hidden" ||
    !navigator.onLine
  )
    return;
  clearReconnect();
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    void reconnect();
  }, reconnectDelay);
  reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
}
async function reconnect() {
  if (disposed || reconnecting || stopping.value || !term.value) return;
  clearReconnect();
  reconnecting = true;
  try {
    // Reattach only to an existing live session. Never start another shell.
    const { sessions } = await api.sessions.listAll();
    if (disposed || document.visibilityState === "hidden") return;
    const session = sessions.find((item) => item.id === sessionId);
    if (!session || session.status === "stopped") {
      const previous = socket;
      socket = null;
      previous?.close();
      errorMessage.value = t("terminal.not_found");
      status.value = "closed";
      return;
    }
    connectStream();
  } catch (err) {
    if (!disposed) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        const previous = socket;
        socket = null;
        previous?.close();
        errorMessage.value = describeError(err);
        status.value = "error";
      } else scheduleReconnect();
    }
  } finally {
    reconnecting = false;
  }
}
function onVisibility() {
  if (document.visibilityState === "hidden") {
    liveness?.suspend();
    clearReconnect();
  } else onResume();
}
function onResume() {
  if (disposed || document.visibilityState === "hidden") return;
  settleLayout();
  if (!term.value || errorMessage.value || stopping.value) return;
  if (socket?.readyState === WebSocket.OPEN) liveness?.probe();
  else if (socket?.readyState !== WebSocket.CONNECTING) void reconnect();
}
function connectStream() {
  if (disposed || !term.value) return;
  // The previous WS can close while reconnect() awaits its HTTP lookup and
  // schedule another retry. A successful replacement supersedes that timer.
  clearReconnect();
  clearTimeout(connectTimer ?? undefined);
  const previous = socket;
  socket = null;
  previous?.close();
  output?.dispose();
  liveness?.dispose();
  const wsPath = targetType === "nx" ? "/ws/nx" : "/ws/remote";
  const ws = new WebSocket(terminalSocketUrl(wsPath, sessionId));
  const health = terminalLiveness(ws, () => {
    if (socket !== ws || disposed || document.visibilityState === "hidden") return;
    ws.close();
    void reconnect();
  });
  liveness = health;
  const decoder = terminalOutput(
    (bytes) => {
      const current = term.value;
      if (socket === ws && !disposed && current)
        return new Promise<void>((resolve) => current.write(bytes, resolve));
    },
    () => ws.close(),
    (frame) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(frame);
    },
  );
  output = decoder;
  ws.binaryType = "arraybuffer";
  socket = ws;
  status.value = "connecting";
  connectTimer = setTimeout(() => {
    if (socket === ws) ws.close();
  }, 10_000);
  ws.onopen = () => {
    if (socket !== ws || disposed) return;
    clearTimeout(connectTimer ?? undefined);
    // The server replays its output on every attach. Reset only after the new
    // connection succeeds, so an offline user can still read the old screen.
    term.value?.reset();
    status.value = "connected";
    reconnectDelay = 1000;
    settleLayout();
  };
  ws.onmessage = (ev) => {
    if (socket !== ws || disposed || !(ev.data instanceof ArrayBuffer)) return;
    if (health.receive(ev.data)) return;
    decoder.push(ev.data);
  };
  ws.onclose = async () => {
    health.dispose();
    await decoder.drain();
    if (socket !== ws || disposed) return;
    clearTimeout(connectTimer ?? undefined);
    status.value = "closed";
    socket = null;
    scheduleReconnect();
  };
  ws.onerror = () => {
    /* close drives retries */
  };
}

async function stopSession() {
  if (!agentId || stopping.value) return;
  stopping.value = true;
  clearReconnect();
  try {
    if (targetType === "nx") {
      await api.nx.stopSession(agentId, sessionId);
    } else {
      await api.sessions.stop(agentId, sessionId);
    }
    // socket will close server-side; redirect back.
    await router.push({ name: "home" });
  } catch (err) {
    errorMessage.value = describeError(err);
  } finally {
    stopping.value = false;
    if (!socket && !disposed) scheduleReconnect();
  }
}

function describeError(err: unknown): string {
  return friendlyError(err, t);
}
</script>

<template>
  <section
    ref="rootEl"
    class="term"
    :class="{ 'term--mobile': isMobile, 'term--fullscreen': fullscreen }"
    :style="isMobile && fullscreen ? viewport.fixedStyle.value : undefined"
  >
    <div class="term__controls">
      <header class="term__bar">
        <span
          class="term__dot"
          :data-tone="status === 'connected' ? 'ok' : status === 'closed' ? 'bad' : 'warn'"
          :title="t(`terminal.status.${status}`)"
        ></span>
        <span class="term__name">{{ sessionLabel }}</span>
        <span class="term__status">{{ t(`terminal.status.${status}`) }}</span>
        <span class="term__spacer"></span>
        <!-- Fullscreen: maximise the terminal (more rows/cols, easier to read
           and type) on desktop and mobile alike. -->
        <button
          type="button"
          class="term__kbd"
          :class="{ 'term__kbd--on': fullscreen }"
          :aria-label="t('terminal.fullscreen')"
          :title="t('terminal.fullscreen')"
          @click="toggleFullscreen"
        >
          ⛶
        </button>
        <button
          v-if="fullscreen && !nativeFullscreen && !standalone && canNativeFullscreen"
          type="button"
          class="term__kbd"
          :aria-label="t('terminal.browser_fullscreen')"
          :title="t('terminal.browser_fullscreen')"
          @click="requestNativeFullscreen"
        >
          ⤢
        </button>
        <button
          v-if="isMobile && canLockOrientation"
          type="button"
          class="term__kbd"
          :class="{ 'term__kbd--on': orientationLocked }"
          :aria-pressed="orientationLocked"
          :aria-label="t('terminal.orientation_lock')"
          :title="t('terminal.orientation_lock')"
          @click="toggleOrientation"
        >
          {{ orientationLocked ? "🔒" : "↻" }}
        </button>
        <button
          type="button"
          class="term__kbd"
          :aria-label="t('terminal.paste')"
          :title="t('terminal.paste')"
          :disabled="status !== 'connected'"
          @click="pasteClipboard"
        >
          📋
        </button>
        <button
          v-if="status === 'closed' && !errorMessage"
          type="button"
          class="term__kbd"
          :aria-label="t('terminal.reconnect')"
          @click="reconnect"
        >
          ↻
        </button>
        <!-- Keyboard toggle lives in the top bar on mobile: a bottom FAB gets
           hidden by the browser's bottom address bar. -->
        <button
          v-if="isMobile"
          type="button"
          class="term__kbd"
          :class="{ 'term__kbd--on': barOpen }"
          :aria-label="t('a11y.keyboard')"
          @click="toggleBar"
        >
          ⌨
        </button>
        <button
          type="button"
          class="term__stop"
          :disabled="!agentId || stopping"
          @click="stopSession"
        >
          {{ stopping ? "…" : t("terminal.stop") }}
        </button>
      </header>
      <!-- Accessory bar: the keys you need first (↑ ↓ ⏎), then Esc/arrows/^C. -->
      <div v-if="isMobile && barOpen" class="kbar">
        <div class="kbar__keys" @pointerdown.prevent>
          <button type="button" class="kbar__key" aria-label="Arrow up" @click="sendKey('\x1b[A')">
            ↑
          </button>
          <button
            type="button"
            class="kbar__key"
            aria-label="Arrow down"
            @click="sendKey('\x1b[B')"
          >
            ↓
          </button>
          <button type="button" class="kbar__key" aria-label="Enter" @click="sendKey('\r')">
            ⏎
          </button>
          <button type="button" class="kbar__key" aria-label="Tab" @click="sendKey('\t')">⇥</button>
          <button type="button" class="kbar__key" aria-label="Escape" @click="sendKey('\x1b')">
            Esc
          </button>
          <button
            type="button"
            class="kbar__key"
            aria-label="Arrow left"
            @click="sendKey('\x1b[D')"
          >
            ←
          </button>
          <button
            type="button"
            class="kbar__key"
            aria-label="Arrow right"
            @click="sendKey('\x1b[C')"
          >
            →
          </button>
          <button type="button" class="kbar__key" @click="sendKey('\x03')">^C</button>
        </div>
        <div class="kbar__right">
          <button
            type="button"
            class="kbar__key kbar__key--accent"
            :class="{ 'kbar__key--on': keyboardOn }"
            aria-label="Keyboard"
            @click="toggleKeyboard"
          >
            ⌨
          </button>
          <button type="button" class="kbar__key" aria-label="Hide controls" @click="toggleBar">
            ✕
          </button>
        </div>
      </div>
    </div>
    <div class="term__screen">
      <p v-if="errorMessage" class="admin-error" role="alert">{{ errorMessage }}</p>
      <p v-if="orientationMessage" class="term__hint" role="status">{{ orientationMessage }}</p>
      <p
        v-if="imageMessage"
        class="term__hint"
        :class="{ 'admin-error': imageError }"
        :role="imageError ? 'alert' : 'status'"
      >
        {{ imageMessage }}
      </p>

      <div class="term__body">
        <div ref="terminalEl" class="term__pane"></div>
      </div>

      <form v-if="pasteOpen" class="term__paste" @submit.prevent="submitPaste">
        <label for="terminal-paste">{{ t("terminal.paste_hint") }}</label>
        <textarea
          id="terminal-paste"
          ref="pasteEl"
          v-model="pasteDraft"
          rows="2"
          :aria-label="t('terminal.paste_text')"
          autocapitalize="off"
          spellcheck="false"
        ></textarea>
        <div>
          <button type="submit" :disabled="status !== 'connected' || !pasteDraft">
            {{ t("terminal.paste") }}
          </button>
          <button type="button" @click="pasteOpen = false">{{ t("terminal.cancel") }}</button>
        </div>
      </form>
    </div>
  </section>
</template>

<style scoped>
.term {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  gap: 0.5rem;
  /* Fill the scroll area (main) exactly — main is the viewport minus the
     header, so the terminal never overflows the page or slides under it. */
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}
.term__controls {
  display: contents;
}
.term__screen {
  grid-row: 2;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: inherit;
}
/* Slim one-line bar: status dot · name · status · spacer · stop. */
.term__bar {
  grid-row: 1;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
}
.term__dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  flex: 0 0 auto;
  background: var(--color-fg-dim);
}
.term__dot[data-tone="ok"] {
  background: var(--color-ok);
}
.term__dot[data-tone="warn"] {
  background: var(--color-warn);
}
.term__dot[data-tone="bad"] {
  background: var(--color-error);
}
.term__name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50vw;
  min-width: 0;
  flex-shrink: 1;
}
.term__status {
  color: var(--color-fg-dim);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.term__spacer {
  flex: 1;
}
.term__body {
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
}
.term__pane {
  flex: 1;
  min-height: 0;
  min-width: 0;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.4rem;
  overflow: hidden;
  /* Finger drag scrolls the terminal's scrollback; keep the page from
     bouncing / pull-to-refresh stealing the gesture. */
  touch-action: pan-y;
  overscroll-behavior: contain;
}
/* Mobile: a JS handler translates finger drags into wheel events (scrollback
   for shell, mouse-wheel for tmux/zellij), so let it own the gesture. */
.term--mobile .term__pane {
  touch-action: none;
}
.term__stop {
  padding: 0.25rem 0.7rem;
  font-size: 0.8rem;
  border-radius: 4px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elev);
  color: var(--color-fg);
  cursor: pointer;
  font-weight: 500;
}
.term__stop:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Fullscreen overlay: cover the whole viewport (desktop + mobile). */
.term--fullscreen {
  position: fixed;
  inset: 0;
  z-index: 100;
  height: 100dvh;
  margin: 0;
  padding: 0.5rem;
  padding-top: max(0.5rem, env(safe-area-inset-top));
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
  background: var(--color-bg);
}

/* ---- mobile: reading-first full-screen terminal ------------------- */
.term--mobile {
  gap: 0.35rem;
  padding-bottom: env(safe-area-inset-bottom);
}
/* Drop the redundant status word on mobile — the dot already says it. */
.term--mobile .term__status {
  display: none;
}

.term__kbd {
  flex: 0 0 auto;
  padding: 0.25rem 0.6rem;
  font-size: 1rem;
  line-height: 1;
  border-radius: 4px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elev);
  color: var(--color-fg);
  cursor: pointer;
}
.term__kbd--on {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.kbar {
  grid-row: 3;
  flex: 0 0 auto;
  min-width: 0;
  display: flex;
  gap: 0.4rem;
  align-items: stretch;
  padding-bottom: env(safe-area-inset-bottom);
}
.kbar__keys {
  min-width: 0;
  flex: 1;
  display: flex;
  gap: 0.35rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.kbar__keys::-webkit-scrollbar {
  display: none;
}
.kbar__right {
  display: flex;
  gap: 0.35rem;
}
.kbar__key {
  min-width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 auto;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elev);
  color: var(--color-fg);
  font-family: var(--font-mono);
  font-size: 0.9rem;
  cursor: pointer;
}
.kbar__key--on {
  border-color: var(--color-accent);
  background: var(--color-accent);
  color: #fff;
}
.kbar__key--accent {
  border-color: var(--color-accent);
  color: var(--color-accent);
}
.kbar__key:active {
  background: var(--color-accent);
  color: #fff;
}
.term__hint {
  margin: 0;
  font-size: 0.8rem;
}
.term__paste {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 0 0 auto;
}
.term__paste textarea {
  width: 100%;
  min-height: 2.5rem;
  max-height: 7rem;
  font-size: 16px;
  resize: vertical;
}
.term__paste button {
  padding: 0.35rem 0.7rem;
  margin-right: 0.5rem;
}
.term--mobile .term__bar {
  gap: 0.25rem;
}
.term--mobile .term__kbd {
  padding: 0.25rem 0.4rem;
  min-height: 36px;
}
.term--mobile .term__stop {
  padding: 0.25rem 0.4rem;
}
/* All controls move beside the terminal on a landscape phone, leaving its
   full height available even above the soft keyboard. The rail scrolls rather
   than shrinking touch targets or pushing the terminal out of the viewport. */
@media (orientation: landscape) {
  .term--mobile {
    display: flex;
    flex-direction: row;
  }
  .term--mobile .term__screen {
    flex: 1;
    order: 0;
  }
  .term--mobile .term__controls {
    display: flex;
    flex-direction: column;
    order: 1;
    flex: 0 0 auto;
    width: calc(5.5rem + 0.35rem + env(safe-area-inset-right));
    min-height: 0;
    gap: 0.35rem;
    padding-right: env(safe-area-inset-right);
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    touch-action: pan-y;
    scrollbar-width: none;
  }
  .term--mobile .term__controls::-webkit-scrollbar {
    display: none;
  }
  .term--mobile .term__bar,
  .term--mobile .kbar__keys,
  .term--mobile .kbar__right {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.35rem;
    flex: 0 0 auto;
    overflow: visible;
  }
  .term--mobile .term__name,
  .term--mobile .term__dot,
  .term--mobile .term__spacer {
    display: none;
  }
  .term--mobile .term__stop {
    grid-column: 1 / -1;
    min-height: 36px;
  }
  .term--mobile .kbar {
    flex-direction: column;
    padding-bottom: 0;
  }
  .term--mobile .term__kbd,
  .term--mobile .kbar__key {
    min-width: 0;
    width: 100%;
  }
}
</style>
